import { useEffect, useMemo, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { toast } from "react-toastify";
import { sanitize } from "dompurify";
import { AnswerRequired, CODE_KEY, QuestionType } from "@fhc/shared/constants";
import SurveyProgressBar from "./SurveyProgressBar";
import SurveyTextboxChoices from "./Choices/SurveyTextboxChoices";
import SurveyRatingChoices from "./Choices/SurveyRatingChoices";
import SurveyMultipleChoices from "./Choices/SurveyMultipleChoices";
import TableSheetChoices from "./Choices/TableSheetChoices";
import SurveySingleSelectChoices from "./Choices/SurveySingleSelectChoices";
import defaultLogo from "../../assets/images/defaultLogo.png";
import SurveyButtonPanel from "./SurveyButtonPanel";
import ReCAPTCHA, { useReCAPTCHA } from "../ReCAPTCHA";
import { COPYRIGHT_MESSAGE } from "../../constants";
import useSurvey from "../../hooks/useSurvey";
import useSurveyNavigation from "../../hooks/useSurveyNavigation";
import { calculateTotalPages } from "../../utils";
import { TABLE_QUESTION_ROWS_PER_PAGE } from "../../constants";
import "../../css/survey.scss";

const PROGRESS_BAR_SIZE = "10px"; // Applied to width when progress bar is vertical and height when it's horizontal

const SurveyQuestions = ({ questions, handleGetResult, themeColors, activeStep, setActiveStep }) => {
    const maxProgressBarValue = useMemo(() => questions.length, [questions.length]);

    const [isFinishButtonLoading, setIsFinishButtonLoading] = useState(false);

    const { reCAPTCHAToken, renderReCAPTCHA, resetReCAPTCHA } = useReCAPTCHA(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY_FOR_SURVEY_SUBMIT,
    );

    const { selections, selectChoice } = useSurvey({
        questionIndex: activeStep,
        questions: questions,
    });

    const { tableQuestionIndices, initializeTableQuestionIndices, navigateTable } = useSurveyNavigation();

    const isLastQuestion = activeStep === questions.length - 1;

    useEffect(() => {
        if (!tableQuestionIndices) {
            initializeTableQuestionIndices(questions);
        }
    }, [questions, tableQuestionIndices, initializeTableQuestionIndices]);

    const validateResponse = (activeStep, questionId, questionCodeKeys) => {
        const response = selections[questionId];

        if (questions[activeStep].required !== AnswerRequired.NOT_REQUIRED_ALLOW_SKIP && !response) {
            return "Please provide at least one response";
        }

        if (
            questionCodeKeys?.includes(CODE_KEY.year_of_birth) &&
            (response < 1900 || response > new Date().getFullYear())
        ) {
            return `Year of birth must be between 1900 and the current year`;
        }

        return null;
    };

    const handleNext = () => {
        const question = questions[activeStep];

        const error = validateResponse(activeStep, question.id, question.code_keys);
        if (error) {
            toast.error(error);
            return;
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        // Invalidate the reCAPTCHA token if there is one
        if (reCAPTCHAToken) {
            resetReCAPTCHA();
        }

        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const getScore = async () => {
        if (!selections[questions[activeStep].id]) {
            return;
        }

        await handleGetResult(selections, reCAPTCHAToken);
    };

    useEffect(() => {
        console.log(selections);
    }, [selections]);

    // medium screen (width >= 768px) is desktop, any smaller is mobile
    const mdScreen = useMediaQuery("(min-width:768px)");
    const sliderProps = {
        track: "normal",
        orientation: mdScreen ? "vertical" : "horizontal",
        trackBackgroundColor: themeColors.secondaryColor,
        railBackgroundColor: themeColors.primaryColor,
        thumbBorderColor: themeColors.primaryColor,
        maxValue: maxProgressBarValue,
        value: activeStep,
        style: mdScreen
            ? { width: PROGRESS_BAR_SIZE, height: "500px", transform: "rotate(180deg)" } // rotate make the progress start from the top instead of the bottom
            : { height: PROGRESS_BAR_SIZE },
    };

    //debug
    useEffect(() => {
        console.debug(questions[activeStep]);
    }, [activeStep]);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "100%",
                gap: "32px",
            }}
        >
            <div style={{ display: "flex" }}>
                {mdScreen && (
                    <SurveyProgressBar
                        style={{
                            width: PROGRESS_BAR_SIZE,
                            height: "500px",
                            transform: "rotate(180deg)",
                        }}
                        {...sliderProps}
                    />
                )}

                <main style={{ marginLeft: mdScreen ? "96px" : "0px" }}>
                    {/* This image is shown when the image in the header is hidden */}
                    <div className="block md:hidden mb-[50px]">
                        <img src={themeColors.mainLogo ? themeColors.mainLogo : defaultLogo} alt="" className="w-20" />
                    </div>

                    <Box display="flex" flexDirection="column" gap="30px">
                        <span
                            className="survey-question-text"
                            dangerouslySetInnerHTML={{
                                __html: sanitize(`${activeStep + 1}. ${questions[activeStep].question}`),
                            }}
                        />

                        {!!questions[activeStep].secondary_question && (
                            <span
                                className="survey-question-text"
                                dangerouslySetInnerHTML={{ __html: sanitize(questions[activeStep].secondary_question) }}
                            />
                        )}
                        <Box>
                            {questions[activeStep].type === QuestionType.SINGLE_RESPONSE ? (
                                <SurveySingleSelectChoices
                                    selections={selections}
                                    question={questions[activeStep]}
                                    selectChoice={selectChoice}
                                    themeColors={themeColors}
                                ></SurveySingleSelectChoices>
                            ) : questions[activeStep].type === QuestionType.TEXTBOX ? (
                                <SurveyTextboxChoices
                                    selections={selections}
                                    question={questions[activeStep]}
                                    selectChoice={selectChoice}
                                    // This information (input type and placeholder) will need to be in the database in the future
                                    // However, it's just hardcoded for now to resolve https://pmgint.atlassian.net/browse/APPS-1102.
                                    // The hardcoding can be replace when doing https://pmgint.atlassian.net/browse/APPS-2461.
                                    inputType={
                                        questions[activeStep].code_keys?.includes(CODE_KEY.year_of_birth)
                                            ? "number"
                                            : "text"
                                    }
                                    placeholder={
                                        questions[activeStep].code_keys?.includes(CODE_KEY.year_of_birth)
                                            ? "Year of birth"
                                            : ""
                                    }
                                    integerOnly={questions[activeStep].code_keys?.includes(CODE_KEY.year_of_birth)}
                                ></SurveyTextboxChoices>
                            ) : questions[activeStep].type === QuestionType.RATING_SCALE ? (
                                <SurveyRatingChoices
                                    selections={selections}
                                    question={questions[activeStep]}
                                    selectChoice={selectChoice}
                                    themeColors={themeColors}
                                ></SurveyRatingChoices>
                            ) : questions[activeStep].type === QuestionType.MULTIPLE_RESPONSE ? (
                                <SurveyMultipleChoices
                                    selections={selections}
                                    question={questions[activeStep]}
                                    selectChoice={selectChoice}
                                    themeColors={themeColors}
                                ></SurveyMultipleChoices>
                            ) : questions[activeStep].type === QuestionType.MATRIX ? (
                                <TableSheetChoices
                                    selections={selections}
                                    question={questions[activeStep]}
                                    selectChoice={selectChoice}
                                    themeColors={themeColors}
                                    pageIndex={tableQuestionIndices[questions[activeStep].id]}
                                    rowsPerPage={TABLE_QUESTION_ROWS_PER_PAGE}
                                ></TableSheetChoices>
                            ) : (
                                ""
                            )}
                        </Box>

                        {isLastQuestion && <ReCAPTCHA render={renderReCAPTCHA} />}

                        <SurveyButtonPanel
                            colorTheme={themeColors}
                            showFinishButton={isLastQuestion}
                            disableFinishButton={!reCAPTCHAToken || !selections[questions[questions.length - 1]?.id]} // Disable the finish button until the reCAPTCHA is valid and an answer is selected
                            isFinishButtonLoading={isFinishButtonLoading}
                            showBackButton={activeStep > 0}
                            handleNext={
                                questions[activeStep].type === QuestionType.MATRIX &&
                                tableQuestionIndices[questions[activeStep].id] <
                                    calculateTotalPages(
                                        TABLE_QUESTION_ROWS_PER_PAGE,
                                        questions[activeStep].choices.data.length,
                                    ) -
                                        1
                                    ? () => navigateTable(questions[activeStep].id, 1)
                                    : handleNext
                            }
                            handleBack={
                                questions[activeStep].type === QuestionType.MATRIX &&
                                tableQuestionIndices[questions[activeStep].id] > 0
                                    ? () => navigateTable(questions[activeStep].id, -1)
                                    : handleBack
                            }
                            handleFinish={async () => {
                                try {
                                    setIsFinishButtonLoading(true);
                                    await getScore();
                                } finally {
                                    setIsFinishButtonLoading(false);
                                }
                            }}
                        />
                    </Box>
                </main>
            </div>

            {!mdScreen && (
                <div>
                    <SurveyProgressBar style={{ height: PROGRESS_BAR_SIZE }} {...sliderProps} />
                    <div className="text-center font-[400] text-[9.02px] leading-[24px] md:hidden">
                        {COPYRIGHT_MESSAGE}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SurveyQuestions;
